/**
 * `/p/:token` — a página que o cliente abre, sem login.
 *
 * É a única tela do sistema que não passa pela Portaria, e a única cujo
 * leitor não é da oficina. Isso muda três coisas em relação a todas as
 * outras:
 *
 *  • NENHUM VALOR aparece aqui. O tipo `ConsultaPublica` não tem campo de
 *    dinheiro — a decisão está no banco, em `04_consulta_publica.sql`, e
 *    esta tela não teria como mostrar mesmo se alguém quisesse. Um link sem
 *    senha circula: vai para o grupo da família, para o print, para quem
 *    pedir;
 *  • sem `AppShell`. Menu lateral e barra de navegação levariam o cliente a
 *    rotas que respondem 401 na cara dele;
 *  • o vocabulário é o do cliente, não o da oficina. "Aguardando
 *    Orçamento" vira uma frase que explica o que está acontecendo e o que
 *    ele deve fazer — o rótulo interno sozinho não diz nada a quem só quer
 *    saber se pode buscar o aparelho.
 *
 * O erro mais provável não é falha de rede: é link revogado ou endereço
 * digitado errado. Por isso o 404 tem tratamento próprio e amigável, sem
 * jargão e sem código de erro.
 */
import { useParams } from '@tanstack/react-router';
import type { CategoriaStatus, ConsultaPublica as Consulta } from '@tcm/shared';
import { useConsultaPublica } from '../features/queries';
import { ApiError } from '../lib/api';
import { Botao, Card, Esqueleto, cx } from '../components/ui';
import { FadeIn } from '../components/animados';
import { tomStatus } from '../lib/paleta';
import { formatarData, descreverDias } from '../lib/format';
import { LOJA } from '../lib/loja';

/**
 * O que cada categoria de status significa PARA O CLIENTE.
 *
 * Mapeia a categoria, e não o código: são cinco frases em vez de quinze, e
 * um status novo no catálogo entra numa delas sem passar por aqui. O texto
 * responde a pergunta que ele veio fazer — "posso buscar?" — em vez de
 * repetir o rótulo interno que ele já viu no topo.
 */
const EXPLICACAO: Record<CategoriaStatus, string> = {
  aguardando: 'Seu aparelho está na fila de avaliação. Assim que houver novidade, a loja entra em contato.',
  andamento: 'Seu aparelho está em atendimento com a nossa equipe técnica.',
  concluido: 'O atendimento foi concluído. A loja avisa quando o aparelho estiver liberado para retirada.',
  pendente: 'O atendimento está aguardando uma etapa — peça, aprovação ou pagamento. A loja entra em contato.',
  cancelado: 'Este atendimento foi encerrado sem reparo. Fale com a loja em caso de dúvida.',
};

export function ConsultaPublica() {
  const { token } = useParams({ from: '/p/$token' });
  const { data, isLoading, error } = useConsultaPublica(token);

  if (isLoading) {
    return (
      <Moldura>
        <Card>
          <Esqueleto linhas={6} />
        </Card>
      </Moldura>
    );
  }

  if (error || !data) return <Moldura><Indisponivel erro={error} /></Moldura>;

  return (
    <Moldura>
      <Ficha consulta={data} />
    </Moldura>
  );
}

/* ------------------------------------------------------------------ */

function Ficha({ consulta }: { consulta: Consulta }) {
  const tom = tomStatus(consulta.status);
  const aparelho = [consulta.aparelho, consulta.marca, consulta.modelo].filter(Boolean).join(' ');

  return (
    <FadeIn>
      <Card className="overflow-hidden p-0 sm:p-0 lg:p-0">
        {/* Filete da cor do status: de longe, no celular, é o que responde
            "mudou alguma coisa?" antes de qualquer leitura. */}
        <div className="h-2 w-full" style={{ backgroundColor: tom.forte }} />

        <div className="p-5 sm:p-8">
          <p className="text-sm font-medium text-slate-500">
            Ordem de Serviço nº {consulta.osNumber}
          </p>
          <h1 className="mt-1 text-2xl font-bold text-slate-800">
            {consulta.nome ? `Olá, ${consulta.nome}` : 'Acompanhamento do seu aparelho'}
          </h1>

          <div
            className="mt-5 rounded-xl px-4 py-4"
            style={{ backgroundColor: tom.suave, color: tom.texto }}
          >
            <p className="text-xs font-semibold uppercase tracking-wide opacity-80">
              Situação atual
            </p>
            <p className="mt-1 text-xl font-bold">{consulta.statusRotulo}</p>
            <p className="mt-2 text-sm leading-relaxed">
              {EXPLICACAO[consulta.statusCategoria]}
            </p>
          </div>

          <dl className="mt-6 grid gap-4 sm:grid-cols-2">
            <Linha rotulo="Aparelho" valor={aparelho || 'Não informado'} />
            <Linha
              rotulo="Deixado na loja em"
              valor={consulta.entrada ? formatarData(consulta.entrada) : 'Não informado'}
            />
            {consulta.defeitoRelatado && (
              <Linha rotulo="Problema relatado" valor={consulta.defeitoRelatado} largura="sm:col-span-2" />
            )}
            {consulta.saida ? (
              <Linha rotulo="Finalizado em" valor={formatarData(consulta.saida)} />
            ) : (
              // Só enquanto está aberta: "há 40 dias" ao lado de um
              // atendimento já encerrado é cobrança sem destinatário.
              consulta.diasEmAberto !== null && (
                <Linha rotulo="Na loja" valor={descreverDias(consulta.diasEmAberto)} />
              )
            )}
          </dl>

          <p className="mt-8 border-t border-slate-200 pt-5 text-sm leading-relaxed text-slate-500">
            Esta página mostra apenas o andamento do serviço e é atualizada automaticamente.
            Para falar sobre valores, prazos ou retirada, entre em contato com a loja
            {LOJA.telefone ? ` pelo telefone ${LOJA.telefone}` : ''}.
          </p>
        </div>
      </Card>
    </FadeIn>
  );
}

function Linha({
  rotulo,
  valor,
  largura,
}: {
  rotulo: string;
  valor: string;
  largura?: string;
}) {
  return (
    <div className={largura}>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">{rotulo}</dt>
      <dd className="mt-1 text-base font-medium text-slate-800">{valor}</dd>
    </div>
  );
}

/**
 * Link que não responde.
 *
 * O 404 é o caso COMUM, não a exceção: link revogado, endereço colado pela
 * metade, token de uma OS antiga. Ele não recebe cara de erro de sistema —
 * recebe uma frase e um caminho de saída. Falha de rede, essa sim, é outra
 * coisa e vale um botão de tentar de novo.
 */
function Indisponivel({ erro }: { erro: unknown }) {
  const ehLinkMorto = erro instanceof ApiError && erro.status === 404;

  return (
    <Card>
      <div className="py-6 text-center">
        <p className="text-4xl" aria-hidden>
          {ehLinkMorto ? '🔗' : '📡'}
        </p>
        <h1 className="mt-4 text-xl font-bold text-slate-800">
          {ehLinkMorto ? 'Link indisponível' : 'Não foi possível carregar'}
        </h1>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-500">
          {ehLinkMorto
            ? 'Este link de acompanhamento não está mais válido. Pode ter sido substituído por um novo — fale com a loja para receber o endereço atualizado.'
            : 'Verifique sua conexão e tente novamente em alguns instantes.'}
        </p>
        {!ehLinkMorto && (
          <Botao className="mt-5" onClick={() => window.location.reload()}>
            Tentar de novo
          </Botao>
        )}
      </div>
    </Card>
  );
}

/**
 * Moldura da página. Um cartão centralizado, sem menu e sem link para
 * dentro do sistema — a única coisa clicável aqui é o botão de recarregar.
 */
function Moldura({ children }: { children: React.ReactNode }) {
  return (
    <div className={cx('flex min-h-screen justify-center bg-creme-100 px-4 py-8 sm:py-12')}>
      <div className="w-full max-w-lg">
        <header className="mb-6 flex items-center justify-center gap-3">
          <img src={LOJA.logo} alt="" aria-hidden className="h-10 w-10 rounded-xl object-contain" />
          <div className="text-center sm:text-left">
            <p className="font-bold text-slate-800">{LOJA.nome}</p>
            <p className="text-xs text-slate-500">{LOJA.atividade}</p>
          </div>
        </header>
        {children}
      </div>
    </div>
  );
}
